import { Link } from "react-router-dom";
import { ExternalLink, Search } from "lucide-react";
import StepShell from "./StepShell";
import { fmtMoney, fmtMiles } from "../../config";

/**
 * Step 2 — Vehicle.
 *
 * Shows the resolved vehicle (photo, year/make/model/trim, stock, VIN,
 * mileage if used, Build My Own Deal price) with two affirmative actions:
 *   • Looks good, continue
 *   • I want a different vehicle
 *
 * Vehicle resolution / prefill routing is handled by the parent (it owns
 * the URL params); we just render what it passes in. DO NOT modify the
 * vehicle prefill routing per project guardrails.
 */
export default function VehicleStep({ byod, vehicle, vehicleStatus, detailHref, onContinue, onBack, firstName }) {
  if (vehicleStatus === "loading") {
    return (
      <StepShell
        title={firstName ? `${firstName}, let's confirm the vehicle.` : "Let's confirm the vehicle."}
        subtitle="Pulling the latest details from inventory..."
        onBack={onBack}
        showContinue={false}
        testIdPrefix="byod-vehicle-step"
      >
        <div className="rounded-2xl bg-white border border-[var(--ssc-line)] shadow-sm p-6 text-[var(--ssc-ink-2)]">
          Loading vehicle details...
        </div>
      </StepShell>
    );
  }

  if (!vehicle) {
    return (
      <StepShell
        title={firstName ? `${firstName}, which vehicle are we building this for?` : "Which vehicle are we building this for?"}
        subtitle="Pick a vehicle and I'll bring you back here with everything pre-filled."
        onBack={onBack}
        showContinue={false}
        testIdPrefix="byod-vehicle-step"
      >
        <div className="rounded-2xl bg-amber-50 border-2 border-amber-200 p-5">
          <div className="text-sm font-extrabold text-amber-900">
            No vehicle selected yet — let&rsquo;s get you one.
          </div>
          <p className="text-sm text-amber-800 mt-1">
            Browse the showroom, then click <strong>Start My Deal</strong> on
            any vehicle&rsquo;s page to come back here with that vehicle pre-loaded.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link to="/new-vehicles" className="ssc-btn ssc-btn-navy">Browse New</Link>
            <Link to="/used-vehicles" className="ssc-btn ssc-btn-outline">Browse Used</Link>
            <Link to="/find-me-a-vehicle" className="ssc-btn ssc-btn-primary">
              <Search size={14} /> Help me find one
            </Link>
          </div>
        </div>
      </StepShell>
    );
  }

  const title = `${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim || ""}`.trim();
  const isUsed = (vehicle.status || "").toLowerCase().includes("used");

  return (
    <StepShell
      title={firstName ? `${firstName}, here's the vehicle you're building this for.` : "Here's the vehicle you're building this for."}
      subtitle="Take a look — if it's not the right one, you can switch any time."
      onBack={onBack}
      onContinue={onContinue}
      continueLabel="Looks good, continue"
      testIdPrefix="byod-vehicle-step"
      extraActions={
        <Link
          to="/inventory"
          className="ssc-btn ssc-btn-outline"
          data-testid="byod-vehicle-different"
        >
          I want a different vehicle
        </Link>
      }
    >
      <div
        className="rounded-2xl bg-white border-2 border-[var(--ssc-blue)] shadow-md overflow-hidden"
        data-testid="byod-vehicle-card"
      >
        <div className="grid md:grid-cols-[280px,1fr] gap-0">
          <Link to={detailHref || "#"} className="block bg-[var(--ssc-silver-2)]">
            {vehicle.photos?.[0]?.url ? (
              <img
                src={vehicle.photos[0].url}
                alt={title}
                className="w-full h-[200px] md:h-full object-cover"
                data-testid="byod-vehicle-photo"
              />
            ) : (
              <div className="w-full h-[200px] md:h-full grid place-items-center text-xs text-[var(--ssc-ink-2)]">
                Photo coming soon
              </div>
            )}
          </Link>
          <div className="p-4 sm:p-5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`ssc-badge ${vehicle.status === "new" ? "ssc-badge-new" : "ssc-badge-used"}`}>
                {vehicle.status === "new" ? "New" : "Used"}
              </span>
              <span className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">
                Build My Own Deal · Vehicle
              </span>
            </div>
            <h3 className="text-xl sm:text-2xl font-extrabold text-[var(--ssc-navy)] leading-tight mt-1" data-testid="byod-vehicle-title">
              {title}
            </h3>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm" data-testid="byod-vehicle-specs">
              <dt className="text-[var(--ssc-ink-2)]">Year</dt><dd className="font-bold text-[var(--ssc-ink)]">{vehicle.year}</dd>
              <dt className="text-[var(--ssc-ink-2)]">Make</dt><dd className="font-bold text-[var(--ssc-ink)]">{vehicle.make}</dd>
              <dt className="text-[var(--ssc-ink-2)]">Model</dt><dd className="font-bold text-[var(--ssc-ink)]">{vehicle.model}</dd>
              {vehicle.trim && (<>
                <dt className="text-[var(--ssc-ink-2)]">Trim</dt><dd className="font-bold text-[var(--ssc-ink)]">{vehicle.trim}</dd>
              </>)}
              <dt className="text-[var(--ssc-ink-2)]">Stock #</dt><dd className="font-mono text-[var(--ssc-ink)]">{vehicle.stock_number || "—"}</dd>
              <dt className="text-[var(--ssc-ink-2)]">VIN</dt><dd className="font-mono text-[var(--ssc-ink)] truncate">{vehicle.vin || "—"}</dd>
              {isUsed && vehicle.mileage != null && (<>
                <dt className="text-[var(--ssc-ink-2)]">Mileage</dt>
                <dd className="font-bold text-[var(--ssc-ink)]">{fmtMiles(vehicle.mileage)}</dd>
              </>)}
            </dl>
            <div className="mt-4 rounded-xl bg-[var(--ssc-paper)] p-3 border border-[var(--ssc-line)]">
              <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">
                Build My Own Deal Price
              </div>
              <div className="text-2xl font-black text-[var(--ssc-navy)]" data-testid="byod-vehicle-price">
                {vehicle.price ? fmtMoney(vehicle.price) : "—"}
              </div>
              <p className="text-[11px] text-[var(--ssc-ink-2)] mt-1 leading-snug">
                Real, live price from this vehicle&rsquo;s listing. Scott will confirm final numbers — taxes,
                fees, and any selected offers are added in later steps.
              </p>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {detailHref && (
                <Link to={detailHref} className="ssc-btn ssc-btn-outline !py-1.5 !text-xs" data-testid="byod-vehicle-vdp-link">
                  <ExternalLink size={13} /> View full vehicle page
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </StepShell>
  );
}
